// Denied & underpaid — pure helpers. One date helper for every deadline.
import type { Claim, Procedure, UnderpaidRow } from './types';
import { isOutOfNetwork } from '@/features/networks/store';

export const APPEALS_USER = { name: 'Ava Thompson', org: 'Lone Star Billing' } as const;

/** Short reason shown on the queue row (board 1), by claim id. */
export const ROW_REASON: Record<string, string> = {
  'CLM-0818': 'Crown called not necessary',
  'CLM-0827': 'Cigna counted a crown on the wrong tooth',
  'CLM-0902': 'Pocket depths were missing',
  'CLM-0908': 'Coverage ended before the visit',
  'CLM-0915': 'Delta says she had a cleaning elsewhere',
  'CLM-0909': 'Paid at the out-of-network fee',
  'CLM-0921': 'Paid as bitewings',
  'CLM-0917': 'Paid as a silver filling',
};
export const PRACTICE = 'Alamo Heights Dental';
export const TODAY_ISO = '2026-10-15';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parse(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

export function formatDateFull(iso: string): string {
  const d = parse(iso);
  if (!d) return iso;
  return `${MONTHS[d.getUTCMonth()] ?? ''} ${String(d.getUTCDate())}, ${String(d.getUTCFullYear())}`;
}

export function formatDateShort(iso: string): string {
  const d = parse(iso);
  if (!d) return iso;
  return `${MONTHS[d.getUTCMonth()] ?? ''} ${String(d.getUTCDate())}`;
}

/** Whole days from today (TODAY_ISO) to the given ISO date. */
export function daysLeft(iso: string, fromIso: string = TODAY_ISO): number {
  const a = parse(fromIso);
  const b = parse(iso);
  if (!a || !b) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/** Add days to an ISO date, return ISO. */
export function addDays(iso: string, days: number): string {
  const d = parse(iso);
  if (!d) return iso;
  d.setUTCDate(d.getUTCDate() + days);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${String(y)}-${m}-${day}`;
}

/** Whole-dollar currency, with cents only when the amount isn't whole. */
export function money(n: number): string {
  const whole = Number.isInteger(n);
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

export function billedOf(procedures: Procedure[]): number {
  return procedures.reduce((s, p) => s + p.fee, 0);
}

const CONTRACT_DENIAL_WORDS: Record<NonNullable<Claim['contractDenial']>, string> = {
  necessity: 'not necessary',
  bundled: 'bundled into another procedure',
  frequency: 'over the frequency limit',
};
const THING_BY_CODE: Record<string, string> = { D2740: 'a crown', D1110: 'a cleaning' };

/** D212: a network contract binds the practice only for an in-network dentist. */
function contractBars(c: Claim): boolean {
  return c.contractDenial !== undefined && !isOutOfNetwork(c.provider, c.payer.short);
}

/** Can the practice offer "Bill the patient" / "The patient will pay" on this claim? */
export function canBillThePatient(c: Claim): boolean {
  return c.canBillPatient !== false && !contractBars(c);
}

/** The line that says why billing the patient isn't offered, or null when no contract rule applies (D212). */
export function noBillReason(c: Claim): string | null {
  if (!c.contractDenial || !contractBars(c)) return null;
  const first = c.procedures[0];
  const thing = first ? (THING_BY_CODE[first.code] ?? first.description.split(',')[0]?.toLowerCase() ?? 'this') : 'this';
  return `Your network contract with ${c.payer.short} doesn’t allow billing ${c.firstName} for ${thing} denied as ${CONTRACT_DENIAL_WORDS[c.contractDenial]}.`;
}

/** Codes in claim order, repeats collapsed as "D4341 ×2". */
export function collapseCodes(procedures: Procedure[]): string {
  const order: string[] = [];
  const count = new Map<string, number>();
  for (const p of procedures) {
    if (!count.has(p.code)) order.push(p.code);
    count.set(p.code, (count.get(p.code) ?? 0) + 1);
  }
  return order
    .map((c) => {
      const n = count.get(c) ?? 1;
      return n > 1 ? `${c} ×${String(n)}` : c;
    })
    .join(', ');
}

/** Expected = your fee × plan% capped at what the plan had left (rule 1). */
export function expectedFor(row: UnderpaidRow, planRemaining: number): { expected: number; capped: boolean } {
  const raw = Math.round(row.yourFee * (row.planPaysPct / 100));
  if (raw > planRemaining) return { expected: planRemaining, capped: true };
  return { expected: raw, capped: false };
}

/** A short wall-clock time for history stamps (mock). */
export function clockNow(): string {
  const d = new Date();
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${String(h)}:${String(d.getMinutes()).padStart(2, '0')} ${ampm}`;
}
